import { useState, useCallback, useMemo, useEffect } from 'react'
import type { MenuItem, Category } from '@/types'
import { getTranslation } from '@/lib/i18n'
import type { Language } from '@/types'

// ============================================================================
// Search Utilities
// ============================================================================

interface SearchOptions {
  threshold?: number // Fuzzy match threshold (0-1)
  searchInDescriptions?: boolean
  searchInCategories?: boolean
}

/**
 * Normalize text for search (remove accents, lowercase)
 */
function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // Remove accents
    .trim()
}

/**
 * Check if text matches query (exact or partial match)
 */
function matchesQuery(text: string, query: string, threshold = 0.3): boolean {
  const normalizedText = normalizeText(text)
  const normalizedQuery = normalizeText(query)

  if (normalizedQuery.length === 0) {
    return true
  }

  // Exact match
  if (normalizedText.includes(normalizedQuery)) {
    return true
  }

  // Partial match with threshold (simple Levenshtein-like check)
  const words = normalizedQuery.split(/\s+/)
  const matchCount = words.filter((word) => normalizedText.includes(word)).length
  const matchRatio = matchCount / words.length

  return matchRatio >= threshold
}

/**
 * Search items across all categories
 */
function searchItems(
  items: ItemWithLocation[],
  query: string,
  language: Language,
  options: SearchOptions = {}
): ItemWithLocation[] {
  const { threshold = 0.3, searchInDescriptions = true } = options

  if (query.trim() === '') {
    return items.filter((entry) => entry.item.available)
  }

  const normalizedQuery = normalizeText(query)

  return items.filter(({ item }) => {
    if (!item.available) {
      return false
    }

    // Search in item name
    const itemName = getTranslation(
      {
        fr: item.name_fr,
        ar: item.name_ar,
        en: item.name_en,
      },
      language
    )

    if (matchesQuery(itemName, normalizedQuery, threshold)) {
      return true
    }

    // Search in description
    if (searchInDescriptions) {
      const itemDesc = getTranslation(
        {
          fr: item.desc_fr,
          ar: item.desc_ar,
          en: item.desc_en,
        },
        language
      )

      if (matchesQuery(itemDesc, normalizedQuery, threshold)) {
        return true
      }
    }

    return false
  })
}

/**
 * Internal type: item paired with its category/subcategory location
 */
interface ItemWithLocation {
  item: MenuItem
  categoryId: string
  subcategoryId: string
}

/**
 * Flatten all items from nested categories structure
 */
function flattenItems(categories: Category[]): ItemWithLocation[] {
  const items: ItemWithLocation[] = []

  categories.forEach((category) => {
    category.subcategories.forEach((subcategory) => {
      subcategory.items.forEach((item) => {
        items.push({
          item,
          categoryId: category.id,
          subcategoryId: subcategory.id,
        })
      })
    })
  })

  return items
}

/**
 * Group flat items back into category structure
 */
function groupItemsByCategory(
  itemsWithLocation: ItemWithLocation[],
  categories: Category[]
  ): Category[] {
  const categoryMap = new Map<string, Category>()

  // Initialize category map
  categories.forEach((category) => {
    categoryMap.set(category.id, {
      ...category,
      subcategories: category.subcategories.map((sub) => ({
        ...sub,
        items: [],
      })),
    })
  })

  // Group items
  itemsWithLocation.forEach(({ item, categoryId, subcategoryId }) => {
    const category = categoryMap.get(categoryId)
    if (category) {
      const subcategory = category.subcategories.find(
        (sub) => sub.id === subcategoryId
      )
      if (subcategory) {
        subcategory.items.push(item)
      }
    }
  })

  // Remove empty subcategories and categories
  return Array.from(categoryMap.values())
    .map((category) => ({
      ...category,
      subcategories: category.subcategories.filter((sub) => sub.items.length > 0),
    }))
    .filter((category) => category.subcategories.length > 0)
}

// ============================================================================
// Hook: useSearch
// ============================================================================

export interface UseSearchReturn {
  query: string
  setQuery: (query: string) => void
  results: Category[]
  resultCount: number
  isSearching: boolean
  clearSearch: () => void
}

export function useSearch(
  categories: Category[],
  language: Language,
  options: SearchOptions = {}
): UseSearchReturn {
  const [query, setQuery] = useState('')

  // Perform search (synchronous - no loading state needed)
  const { results, resultCount } = useMemo(() => {
    if (query.trim() === '') {
      return {
        results: categories,
        resultCount: 0,
      }
    }

    const allItems = flattenItems(categories)
    const matchingItems = searchItems(allItems, query, language, options)
    const groupedResults = groupItemsByCategory(matchingItems, categories)

    return {
      results: groupedResults,
      resultCount: matchingItems.length,
    }
  }, [query, categories, language, options])

  const clearSearch = useCallback(() => {
    setQuery('')
  }, [])

  return {
    query,
    setQuery,
    results,
    resultCount,
    isSearching: false,
    clearSearch,
  }
}

// ============================================================================
// Hook: useSearchDebounced (debounced search for better performance)
// ============================================================================

export function useSearchDebounced(
  categories: Category[],
  language: Language,
  delay = 300,
  options: SearchOptions = {}
): UseSearchReturn {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  // Debounce the query value
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
    }, delay)

    return () => clearTimeout(timer)
  }, [query, delay])

  // Perform search on the debounced query
  const { results, resultCount } = useMemo(() => {
    if (debouncedQuery.trim() === '') {
      return { results: categories, resultCount: 0 }
    }

    const allItems = flattenItems(categories)
    const matchingItems = searchItems(allItems, debouncedQuery, language, options)
    const groupedResults = groupItemsByCategory(matchingItems, categories)

    return {
      results: groupedResults,
      resultCount: matchingItems.length,
    }
  }, [debouncedQuery, categories, language, options])

  const clearSearch = useCallback(() => {
    setQuery('')
    setDebouncedQuery('')
  }, [])

  return {
    query,
    setQuery,
    results,
    resultCount,
    isSearching: query !== debouncedQuery,
    clearSearch,
  }
}
